import { useState } from "react";
import { peekPostLoginRedirectPath } from "../lib/authStorage";
import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import {
  AuthModeForm,
  type AuthChallengeContext,
  type AuthMode,
} from "../components/auth/AuthModeForm";
import { BrandLockup } from "../components/brand/BrandLockup";

type AuthPageProps = {
  notice: string | null;
  onNoticeChange: (notice: string | null) => void;
  navigate: (route: string) => void;
};

const AUTH_MODE_COPY: Record<AuthMode, { title: string; description: string }> =
  {
    login: {
      title: "Welcome back",
      description: "Sign in to turn customer signals into prioritized action.",
    },
    signup: {
      title: "Create your account",
      description:
        "Create your IO Spoke workspace for coordinated customer action.",
    },
    verify: {
      title: "Verify your email",
      description:
        "Enter the verification code we emailed you to finish setup.",
    },
    magic: {
      title: "Use a magic link",
      description: "Secure sign-in to your existing IO Spoke account.",
    },
    mfa: {
      title: "Verify your sign-in",
      description: "Enter the 6-digit code from your authenticator app.",
    },
    new_password: {
      title: "Choose a new password",
      description: "Replace your temporary password to finish signing in.",
    },
    forgot: {
      title: "Reset your password",
      description:
        "Enter your account email and we’ll send a verification code.",
    },
    reset: {
      title: "Choose a new password",
      description: "Enter the code from your email and choose a new password.",
    },
  };

type InitialAuthState = {
  mode: AuthMode;
  email: string;
  verificationCode: string;
  magicToken: string;
};

function getInitialAuthState(): InitialAuthState {
  const params = new URLSearchParams(window.location.search);
  const requestedMode = params.get("mode");
  const code = params.get("code") ?? "";
  const token = params.get("token") ?? "";
  const emailParam = params.get("email");
  const email =
    emailParam && !["{username}", "{email}"].includes(emailParam)
      ? emailParam
      : (localStorage.getItem("auth.pendingVerificationEmail") ?? "");

  if (requestedMode === "signup")
    return { mode: "signup", email, verificationCode: "", magicToken: "" };
  if (requestedMode === "magic" || token)
    return { mode: "magic", email, verificationCode: "", magicToken: token };
  if (requestedMode === "verify" || code || emailParam)
    return { mode: "verify", email, verificationCode: code, magicToken: "" };
  const pendingRedirect = peekPostLoginRedirectPath() ?? "";
  if (pendingRedirect.startsWith("/invite"))
    return { mode: "signup", email, verificationCode: "", magicToken: "" };
  return { mode: "login", email, verificationCode: "", magicToken: "" };
}

export function AuthPage({ notice, onNoticeChange, navigate }: AuthPageProps) {
  const [initialState] = useState(getInitialAuthState);
  const [mode, setMode] = useState<AuthMode>(initialState.mode);
  const [email, setEmail] = useState(initialState.email);
  const [challenge, setChallenge] = useState<AuthChallengeContext | null>(null);

  const changeMode = (nextMode: AuthMode) => {
    onNoticeChange(null);
    setMode(nextMode);
  };

  const handleChallenge = (nextChallenge: AuthChallengeContext) => {
    setChallenge(nextChallenge);
    setEmail(nextChallenge.email);
    setMode(nextChallenge.type);
    onNoticeChange(
      nextChallenge.type === "new_password"
        ? "Choose a permanent password to finish signing in."
        : "Enter the 6-digit code from your authenticator app.",
    );
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-background text-foreground">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_12%,hsl(var(--brand-blue)/0.13),transparent_32rem),radial-gradient(circle_at_88%_85%,hsl(var(--brand-magenta)/0.10),transparent_30rem)] dark:opacity-70"
        aria-hidden="true"
      />
      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-lg flex-col justify-center px-6 py-12 sm:py-16">
        <div className="mb-8 flex flex-col items-center text-center">
          <span className="inline-flex px-3 py-2">
            <BrandLockup alt="IO Spoke" className="h-10 w-auto" />
          </span>
          <p className="mt-3 text-sm font-medium text-muted-foreground">
            Turn conversations into action.
          </p>
        </div>
        <Card className="w-full border-border/80 bg-card/90 shadow-2xl shadow-brand-navy/10 backdrop-blur dark:shadow-black/50">
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl">
              {AUTH_MODE_COPY[mode].title}
            </CardTitle>
            <CardDescription>
              {AUTH_MODE_COPY[mode].description}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AuthModeForm
              key={mode}
              mode={mode}
              email={email}
              initialVerificationCode={initialState.verificationCode}
              initialMagicToken={initialState.magicToken}
              challenge={challenge}
              notice={notice}
              onEmailChange={setEmail}
              onNoticeChange={onNoticeChange}
              onModeChange={changeMode}
              onChallenge={handleChallenge}
              navigate={navigate}
            />
          </CardContent>
          <CardFooter className="flex flex-col gap-3 border-t border-border/60 pt-5 text-sm">
            {mode === "login" ? (
              <div className="w-full space-y-3">
                <p className="text-center text-muted-foreground">
                  New to IO Spoke?
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => changeMode("signup")}
                >
                  Create an account
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => changeMode("login")}
              >
                Back to sign in
              </Button>
            )}
            {mode === "login" ? (
              <button
                type="button"
                className="min-h-[44px] px-3 text-sm font-semibold text-foreground/90 transition hover:text-brand-blue"
                onClick={() => changeMode("magic")}
              >
                Use a magic link
              </button>
            ) : null}
          </CardFooter>
        </Card>
      </div>
    </main>
  );
}
